// World chat: real players only. Refreshes every few seconds while open.
import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api.ts';
import { useGame, ago } from '../state.tsx';
import { PlayerLink } from '../components/ui.tsx';
import { Portrait } from '../art/Portrait.tsx';
import { play } from '../fx/sound.ts';
import { getSettings } from '../fx/settings.ts';

export const CHAT_SEEN_KEY = 'mm-chat-seen';

export function ChatPage() {
  const { me, setFlash, user } = useGame();
  const [messages, setMessages] = useState<any[]>([]);
  const [text, setText] = useState('');
  const [people, setPeople] = useState<{ id: number; name: string; portrait: number }[]>([]);
  const lastId = useRef(0);
  const meId = useRef<number>(me?.id ?? 0);
  const meName = useRef<string>(me?.name ?? '');
  meId.current = me?.id ?? 0;
  meName.current = me?.name ?? '';
  const input = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLDivElement>(null);

  const load = async () => {
    try {
      const r = await api.get(`/api/chat?after=${lastId.current}`);
      if (r.messages.length) {
        // Sound for messages from other people (not on the first load of the page).
        if (lastId.current > 0 && getSettings().alerts) {
          const others = r.messages.filter((m: any) => m.authorId !== meId.current);
          if (others.some((m: any) => meName.current && mentionsMe(m.text, meName.current))) play('mention');
          else if (others.length) play('pop');
        }
        lastId.current = r.messages[r.messages.length - 1].id;
        setMessages((m) => [...m, ...r.messages].slice(-200));
        try { localStorage.setItem(CHAT_SEEN_KEY, String(lastId.current)); } catch { /* storage unavailable */ }
        setTimeout(() => box.current?.scrollTo(0, box.current.scrollHeight), 20);
      }
    } catch { /* try again next tick */ }
  };
  useEffect(() => {
    api.get('/api/chat/people').then((r) => setPeople(r.players ?? [])).catch(() => {});
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, []);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const r = await api.post('/api/chat', { text });
      play('pop');
      setText('');
      const tagged = r?.result?.mentioned ?? r?.mentioned;
      if (tagged?.length) setFlash({ kind: 'good', text: `Notified ${tagged.join(', ')}.` });
      load();
    } catch (err) {
      play('error');
      setFlash({ kind: 'bad', text: err instanceof ApiError ? err.message : 'Could not send.' });
    }
  };

  // @mention autocomplete: suggest names while the word being typed starts with "@".
  const names = people.map((p) => p.name);
  const partial = /(?:^|\s)@([^@]{0,24})$/.exec(text);
  const suggestions = partial ? people.filter((p) => p.id !== me?.id && p.name.toLowerCase().startsWith(partial[1].toLowerCase())).slice(0, 6) : [];
  const pick = (name: string) => {
    setText(text.slice(0, text.length - partial![1].length) + name + ' ');
    input.current?.focus();
  };

  return (
    <div>
      <h1 className="page-title">World Chat</h1>
      <p className="muted small">Talk with the other real players in town. Computer-controlled players don’t chat. Type <b>@</b> and a name to mention someone; they’ll get a notification. Be nice; the admin can delete messages.</p>
      <div className="chat-box" ref={box}>
        {messages.length === 0 ? <div className="muted">No messages yet. Say hello!</div> : messages.map((m) => (
          <div key={m.id} className={`chat-line ${m.authorId === me?.id ? 'mine' : ''} ${me && mentionsMe(m.text, me.name) ? 'mentions-me' : ''}`}>
            <Portrait seed={m.authorPortrait} playerId={m.authorId} size={26} />
            <div className="grow">
              <PlayerLink id={m.authorId} name={m.authorName} /> <span className="dim small">L{m.authorLevel} · {ago(m.createdAt)}</span>
              {user?.role === 'admin' && <> <a className="small dim" onClick={async () => { await api.post('/api/chat/delete', { messageId: m.id }); setMessages((x) => x.filter((y) => y.id !== m.id)); }}>delete</a></>}
              <div className="chat-text">{highlight(m.text, names, me?.name)}</div>
            </div>
          </div>
        ))}
      </div>
      {suggestions.length > 0 && (
        <div className="mention-menu">
          {suggestions.map((p) => (
            <a key={p.id} onClick={() => pick(p.name)}><Portrait seed={p.portrait} playerId={p.id} size={18} /> {p.name}</a>
          ))}
        </div>
      )}
      <form onSubmit={send} className="row" style={{ marginTop: 6 }}>
        <input ref={input} className="grow" value={text} maxLength={300} onChange={(e) => setText(e.target.value)} placeholder="Type a message and press Enter" autoFocus />
        <button className="btn">Send</button>
      </form>
    </div>
  );
}

function mentionsMe(text: string, myName: string): boolean {
  return text.toLowerCase().includes(`@${myName.toLowerCase()}`);
}

/** Wraps "@Name" for known players in a highlight. */
function highlight(text: string, names: string[], myName?: string) {
  if (!text.includes('@') || names.length === 0) return text;
  const sorted = [...names].sort((a, b) => b.length - a.length);
  const out: React.ReactNode[] = [];
  let rest = text;
  let key = 0;
  while (rest.length) {
    const at = rest.indexOf('@');
    if (at < 0) break;
    const lower = rest.slice(at + 1).toLowerCase();
    const name = sorted.find((n) => lower.startsWith(n.toLowerCase()) && !/[a-z0-9]/i.test(lower.charAt(n.length)));
    if (!name) {
      out.push(rest.slice(0, at + 1));
      rest = rest.slice(at + 1);
      continue;
    }
    out.push(rest.slice(0, at));
    out.push(<span key={key++} className={`mention ${name === myName ? 'me' : ''}`}>@{rest.slice(at + 1, at + 1 + name.length)}</span>);
    rest = rest.slice(at + 1 + name.length);
  }
  out.push(rest);
  return out;
}
